import CloseIcon from '@mui/icons-material/Close'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { appointmentsApi } from '../../osa-scheduling/api/appointments.api'
import type { Appointment } from '../../osa-scheduling/types'
import { exportIncompleteProjects } from '../utils/export.utils'

interface IncompleteProjectsDialogProps {
  open: boolean
  onClose: () => void
  incompleteCount: number
}

const IncompleteProjectsDialog: React.FC<IncompleteProjectsDialogProps> = ({
  open,
  onClose,
  incompleteCount,
}) => {
  const navigate = useNavigate()
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  useEffect(() => {
    if (!open) return

    const fetchIncompleteAppointments = async () => {
      setLoading(true)
      setError(null)
      try {
        // Fetch dispatched appointments (incomplete projects)
        const response = await appointmentsApi.getAppointments({
          status: 'dispatched',
          sortField: 'scheduleStartTime',
          sortOrder: 'asc',
          pageSize: incompleteCount,
          env: 'live',
        })

        setAppointments(response.data)
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load incomplete appointments'
        setError(errorMessage)
        console.error('Error fetching incomplete appointments:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchIncompleteAppointments()
  }, [open, incompleteCount])

  const formatDateTime = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm')
    } catch (error) {
      console.error('Error formatting date:', error)
      return '-'
    }
  }

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleExportClose = () => {
    setAnchorEl(null)
  }

  const handleExport = (format: 'csv' | 'xlsx') => {
    exportIncompleteProjects(appointments, format)
    handleExportClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          width: '90vw',
          maxWidth: '1200px',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Incomplete Projects</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {appointments.length > 0 && !loading && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportClick}
                  sx={{ mr: 1 }}
                >
                  Export
                </Button>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleExportClose}>
                  <MenuItem onClick={() => handleExport('xlsx')}>Export as Excel (.xlsx)</MenuItem>
                  <MenuItem onClick={() => handleExport('csv')}>Export as CSV (.csv)</MenuItem>
                </Menu>
              </>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ py: 2 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && appointments.length === 0 && (
          <Typography sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
            No incomplete projects found
          </Typography>
        )}

        {!loading && !error && appointments.length > 0 && (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Scout</TableCell>
                    <TableCell>Scheduled At</TableCell>
                    <TableCell>Confirmation Sent At</TableCell>
                    <TableCell>Reminder Email Sent At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appointments.map(appointment => (
                    <TableRow
                      key={appointment.uuid}
                      component={Link}
                      to={`/osa-scheduling/show/${appointment.uuid}`}
                      sx={{
                        cursor: 'pointer',
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {appointment.customerName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const address = appointment.customerAddress
                          if (!address || address === '-') {
                            return <Typography variant="body2">-</Typography>
                          }
                          const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
                          return (
                            <MuiLink
                              href={googleMapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              sx={{
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              {address}
                            </MuiLink>
                          )
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const { scoutName, scoutUUID } = appointment
                          if (!scoutName || scoutName === '-' || !scoutUUID) {
                            return <Typography variant="body2">-</Typography>
                          }
                          return (
                            <MuiLink
                              href={`/scouts/show/${scoutUUID}`}
                              onClick={e => {
                                e.stopPropagation()
                                navigate(`/scouts/show/${scoutUUID}`)
                                e.preventDefault()
                              }}
                              sx={{
                                color: 'primary.main',
                                textDecoration: 'none',
                                cursor: 'pointer',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              {scoutName}
                            </MuiLink>
                          )
                        })()}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {appointment.scheduleStartTime
                            ? formatDateTime(appointment.scheduleStartTime)
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {appointment.confirmationEmailSent
                            ? formatDateTime(appointment.confirmationEmailSent)
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {appointment.reminderEmailSent
                            ? formatDateTime(appointment.reminderEmailSent)
                            : '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default IncompleteProjectsDialog
